import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import emailService, { type ReceivedEmailListItem, type ReceivedEmail } from '../../services/emailService';

export default function EmailInbox() {
  const [emails, setEmails] = useState<ReceivedEmailListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState<ReceivedEmail | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const data = await emailService.getInbox();
      setEmails(data);
    } catch (error) {
      console.error('Erreur chargement emails:', error);
      toast.error('Erreur lors du chargement des emails');
    } finally {
      setLoading(false);
    }
  };

  const fetchNewEmails = async () => {
    setFetching(true);
    try {
      const result = await emailService.fetchNewEmails(50, false);
      toast.success(`${result.fetched} nouveaux emails récupérés`);
      await loadEmails(); // Recharger la liste
    } catch (error) {
      console.error('Erreur récupération emails:', error);
      toast.error('Erreur lors de la récupération des emails');
    } finally {
      setFetching(false);
    }
  };

  const selectEmail = async (emailListItem: ReceivedEmailListItem) => {
    try {
      // Récupérer les détails complets de l'email
      const fullEmail = await emailService.getReceivedEmailById(emailListItem.id);
      setSelectedEmail(fullEmail);
      
      // Marquer comme lu si non lu
      if (!emailListItem.is_read) {
        await emailService.markAsRead(emailListItem.id);
        setEmails(emails.map(e => 
          e.id === emailListItem.id ? { ...e, is_read: true } : e
        ));
      }
    } catch (error) {
      console.error('Erreur chargement email:', error);
      toast.error('Erreur lors du chargement de l\'email');
    }
  };

  const toggleStar = async (emailId?: string) => {
    if (!emailId) {
      toast.error('Email introuvable');
      return;
    }
    try {
      const updated = await emailService.toggleStar(emailId);
      setEmails(emails.map(e => 
        e.id === emailId ? { ...e, is_starred: updated.is_starred } : e
      ));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(updated);
      }
      toast.success(updated.is_starred ? 'Email marqué' : 'Marque retirée');
    } catch (error) {
      console.error('Erreur toggle star:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const deleteEmail = async (emailId?: string) => {
    if (!emailId) {
      toast.error('Email introuvable');
      return;
    }
    if (!window.confirm('Supprimer cet email ?')) return;
    try {
      await emailService.deleteReceivedEmail(emailId);
      setEmails((prev) => prev.filter((e) => e.id !== emailId));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      toast.success('Email supprimé');
    } catch (error) {
      console.error('Erreur suppression email:', error);
      toast.error('Suppression impossible');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Liste des emails */}
      <div className="lg:col-span-1">
        <div className="card bg-white shadow-xl">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <h2 className="card-title text-warning">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                Emails reçus
              </h2>
              
              <button
                onClick={fetchNewEmails}
                disabled={fetching}
                className="btn btn-sm btn-warning"
                title="Récupérer les nouveaux emails"
              >
                {fetching ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <span className="loading loading-spinner loading-lg text-warning"></span>
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p>Aucun email reçu</p>
                <button onClick={fetchNewEmails} className="btn btn-sm btn-warning mt-3">
                  Récupérer les emails
                </button>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {emails.map((email) => (
                  <div
                    key={email.id}
                    onClick={() => selectEmail(email)}
                    className={`p-3 rounded-lg cursor-pointer transition-all border-2 ${
                      selectedEmail?.id === email.id
                        ? 'bg-yellow-50 border-warning'
                        : email.is_read
                        ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        : 'bg-white border-yellow-200 hover:bg-yellow-50 font-semibold'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!email.is_read && (
                        <span className="badge badge-xs badge-warning mt-1"></span>
                      )}
                      {email.is_starred && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStar(email.id);
                          }}
                          className="text-warning hover:text-yellow-600"
                        >
                          ★
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {email.from_name || email.from_email}
                        </p>
                        <p className={`text-sm truncate ${!email.is_read ? 'font-semibold' : 'text-gray-600'}`}>
                          {email.subject}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(email.received_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteEmail(email.id);
                        }}
                        className="btn btn-ghost btn-xs text-error hover:bg-red-50"
                        title="Supprimer cet email"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Détail de l'email */}
      <div className="lg:col-span-2">
        <div className="card bg-white shadow-xl">
          <div className="card-body">
            {selectedEmail ? (
              <>
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900 flex-1">
                      {selectedEmail.subject}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {new Date(selectedEmail.received_at).toLocaleString('fr-FR', {
                          dateStyle: 'long',
                          timeStyle: 'short',
                        })}
                      </span>
                      <button
                        onClick={() => toggleStar(selectedEmail?.id)}
                        className={`btn btn-sm ${selectedEmail.is_starred ? 'btn-warning' : 'btn-ghost'}`}
                        title={selectedEmail.is_starred ? 'Retirer l\'étoile' : 'Marquer avec une étoile'}
                      >
                        {selectedEmail.is_starred ? '★' : '☆'}
                      </button>
                      <button
                        onClick={() => deleteEmail(selectedEmail.id)}
                        className="btn btn-error btn-xs"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="prose max-w-none">
                  {selectedEmail.body_html ? (
                    <div dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
                  ) : (
                    <p className="whitespace-pre-wrap text-gray-700">
                      {selectedEmail.body_text}
                    </p>
                  )}
                </div>

                {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      Pièces jointes
                    </h4>
                    <div className="space-y-2">
                      {selectedEmail.attachments.map((attachment) => (
                        <a
                          key={attachment.id}
                          href={attachment.file_path}
                          download={attachment.filename}
                          className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
                        >
                          <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="flex-1 font-medium text-gray-900">{attachment.filename}</span>
                          <span className="text-sm text-gray-600">
                            {(attachment.file_size / 1024).toFixed(1)} KB
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <svg className="w-24 h-24 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-lg">Sélectionnez un email pour voir son contenu</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
